import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { GoogleMap, MarkerF, CircleF, InfoWindowF, OverlayView, DirectionsRenderer } from '@react-google-maps/api';
import { motion } from 'framer-motion';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const mapOptions = (MAP_ID) => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapId: MAP_ID,
});

const markerSVGs = {
    Food: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">🍕</text></svg>`,
    'Elderly Care': `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">❤️</text></svg>`,
    Environment: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">🌳</text></svg>`,
    Education: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">📚</text></svg>`,
    'Animal Welfare': `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">🐾</text></svg>`,
    Health: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">⚕️</text></svg>`,
    Others: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40"><text y=".9em" font-size="90">🙌</text></svg>`,
};

const completedMarkerIconSVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="30" height="30"><text y=".9em" font-size="90">⭐</text></svg>`;

const AnimatedMarker = ({ request, onMarkerClick }) => {
    const iconUrl = markerSVGs[request.category] || markerSVGs['Others'];
    return (
        <OverlayView
            position={request.location}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
            <div style={{ transform: 'translate(-50%, -50%)' }}>
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [1, 1.5, 1], rotate: 360 }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    onClick={() => onMarkerClick && onMarkerClick(request)}
                    style={{ cursor: 'pointer', transformOrigin: 'center center' }}
                >
                    <img src={iconUrl} alt={request.category} style={{ width: '40px', height: '40px' }} />
                </motion.div>
            </div>
        </OverlayView>
    );
};

const LiveVolunteerMarker = ({ start, end, map }) => {
    const [currentPos, setCurrentPos] = useState(start);
    
    useEffect(() => {
        if (!map || !start || !end) return;

        const projection = map.getProjection();
        if (!projection) return;

        const startPoint = projection.fromLatLngToPoint(new window.google.maps.LatLng(start));
        const endPoint = projection.fromLatLngToPoint(new window.google.maps.LatLng(end));

        const duration = 15000; // 15 seconds
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            const fraction = Math.min(elapsedTime / duration, 1);

            const newX = startPoint.x + (endPoint.x - startPoint.x) * fraction;
            const newY = startPoint.y + (endPoint.y - startPoint.y) * fraction;

            const newLatLng = projection.fromPointToLatLng(new window.google.maps.Point(newX, newY));
            
            setCurrentPos({ lat: newLatLng.lat(), lng: newLatLng.lng() });

            if (fraction < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);

    }, [map, start, end]);

    return (
        <MarkerF
            position={currentPos}
            icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: '#FFFFFF',
                fillOpacity: 1,
                strokeColor: 'black',
                strokeWeight: 1,
            }}
            zIndex={1000}
        />
    );
};


// --- Main Map Component ---
const MapComponent = ({ isLoaded, loadError, requests = [], completedRequests = [], userLocation, center, onMarkerClick, onCompletedMarkerClick, celebrationRequest, impactAlert, directions, liveVolunteer, onMapClick, singleMarkerPosition, MAP_ID, onLoad, onDrag }) => {
    const [map, setMap] = useState(null);

    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
        if (onLoad) {
            onLoad(mapInstance);
        }
    }, [onLoad]);

    const getMarkerIcon = (category) => {
        if (!isLoaded) return null;
        return {
            url: markerSVGs[category] || markerSVGs['Others'],
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
        };
    };

    const completedMarkerIcon = useMemo(() => {
        if (!isLoaded) return null;
        return {
            url: completedMarkerIconSVG,
            scaledSize: new window.google.maps.Size(30, 30),
            anchor: new window.google.maps.Point(15, 15),
        };
    }, [isLoaded]);

    if (loadError) {
        return <div className="flex items-center justify-center h-full bg-gray-200 text-red-600 p-4 text-center">Error loading map. Please ensure your API key is correct.</div>;
    }

    if (!isLoaded) {
        return <div className="flex items-center justify-center h-full bg-gray-200">Loading Map...</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={12}
            options={mapOptions(MAP_ID)}
            onClick={onMapClick}
            onLoad={onMapLoad}
            onDrag={onDrag}
        >
            {userLocation && (
                <>
                    <MarkerF 
                        position={userLocation} 
                        title={'You are here'}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: '#4285F4',
                            fillOpacity: 1,
                            strokeColor: 'white',
                            strokeWeight: 2,
                        }}
                        zIndex={999}
                    />
                    <CircleF
                        center={userLocation}
                        radius={5000}
                        options={{
                            strokeColor: '#FFFFFF',
                            strokeOpacity: 0.3,
                            strokeWeight: 2,
                            fillColor: '#FFFFFF',
                            fillOpacity: 0.05,
                        }}
                    />
                </>
            )}

            {!directions && requests.map(request => {
                if (request.isNew) {
                    return <AnimatedMarker key={request._id} request={request} onMarkerClick={onMarkerClick} />;
                }
                return (
                    <MarkerF
                        key={request._id}
                        position={request.location}
                        onClick={() => onMarkerClick && onMarkerClick(request)}
                        icon={getMarkerIcon(request.category)}
                    />
                );
            })}
            {!directions && completedRequests.map(request => (
                <MarkerF
                    key={`completed-${request._id}`}
                    position={request.location}
                    onClick={() => onCompletedMarkerClick && onCompletedMarkerClick(request)}
                    icon={completedMarkerIcon}
                    zIndex={-1}
                />
            ))}
            
            {directions && (
                <DirectionsRenderer 
                    directions={directions}
                    options={{
                        suppressMarkers: true,
                        polylineOptions: {
                            strokeColor: '#4285F4',
                            strokeOpacity: 0.8,
                            strokeWeight: 6,
                        }
                    }}
                />
            )}

            {celebrationRequest && (
                <InfoWindowF position={celebrationRequest.location}>
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="bg-white p-4 rounded-lg shadow-lg text-center"
                    >
                        <p className="text-2xl mb-2">🎉</p>
                        <p className="font-bold text-gray-800">Well done!</p>
                        <p className="text-gray-600">You earned {celebrationRequest.points} points here.</p>
                    </motion.div>
                </InfoWindowF>
            )}

            {impactAlert && (
                 <InfoWindowF position={impactAlert.location}>
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="bg-black/80 text-white p-4 rounded-lg shadow-lg text-center"
                    >
                        <p className="text-2xl mb-2">✨</p>
                        <p className="font-bold">Impact Made!</p>
                    </motion.div>
                </InfoWindowF>
            )}
            
            {liveVolunteer && (
                <LiveVolunteerMarker start={liveVolunteer.start} end={liveVolunteer.end} map={map} />
            )}

            {singleMarkerPosition && (
                 <MarkerF
                    position={singleMarkerPosition}
                 />
            )}
        </GoogleMap>
    );
};

export default MapComponent;
